export default class LocationSearchStore {
  constructor(eventBus, locations = [], options = {}) {
    this.eventBus = eventBus;
    this.locations = locations;
    this.options = options;

    // Internal state
    this._activeTypeValue = null;
    this._minimumCapacity = 0;
    this._selectedLineup = null;
  }

  setAnalytics(analytics) {
    this.analytics = analytics;
  }

  get activeTypeValue() {
    return this._activeTypeValue;
  }

  setActiveTypeValue(newValue) {
    this._activeTypeValue = newValue;
    this._selectedLineup = null;
    this.eventBus.emit("locationTypeFiltersChanged", newValue);
  }

  get minimumCapacity() {
    return this._minimumCapacity;
  }

  setMinimumCapacity(newValue) {
    const val = parseInt(newValue, 10);
    this._minimumCapacity = isNaN(val) ? 0 : val;
    this.eventBus.emit("locationMinimumCapacityChanged", this._minimumCapacity);
  }

  get selectedLineup() {
    return this._selectedLineup;
  }

  setSelectedLineup(newValue) {
    if (this._selectedLineup === newValue) {
      this._selectedLineup = null;
    } else {
      this._selectedLineup = newValue;
    }
    this.eventBus.emit("locationSelectedLineupChanged", this._selectedLineup);
  }

  getActiveLocationsWithCurrentType() {
    if (!this._activeTypeValue) return [];
    return this.locations.filter(
      (loc) => loc.isLocationOfActiveType(this._activeTypeValue)
    );
  }

  getActiveLocations() {
    if (!this._activeTypeValue) return [];
    return this.locations.filter((loc) =>
      loc.isActiveLocation(
        this._activeTypeValue,
        this._minimumCapacity,
        this._selectedLineup
      )
    );
  }

  getCapacities() {
    const relevantLocations = this.getActiveLocationsWithCurrentType();
    const capacities = relevantLocations
      .map((location) => {
        if (this._selectedLineup) {
          const venue = location.getSelectedVenueCapacity(this._selectedLineup);
          return venue
            ? parseInt(venue["venue-layout-capacity"], 10)
            : null;
        }
        return location.maxCapacity;
      })
      .filter((c) => c !== null && !isNaN(c))
      .sort((a, b) => a - b);

    if (this.options.customMaxCapacity && this.options.customMaxCapacity > 0) {
      return capacities.filter(cap => cap <= this.options.customMaxCapacity);
    }

    return capacities;
  }

  getVenueCapacities() {
    const relevantLocations = this.getActiveLocationsWithCurrentType();
    const capacities = relevantLocations.flatMap((loc) => loc.venueCapacities || []);
    const uniqueMap = new Map();

    capacities.forEach((vc) => {
      const layout = vc["venue-layout"];
      const cap = parseInt(vc["venue-layout-capacity"], 10);
      
      if (this.options.customMaxCapacity > 0 && cap > this.options.customMaxCapacity) {
        return;
      }
      
      if (!uniqueMap.has(layout) || uniqueMap.get(layout) < cap) {
        uniqueMap.set(layout, cap);
      }
    });

    return Array.from(uniqueMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([layout, capacity]) => ({
        "venue-layout": layout,
        "venue-layout-capacity": capacity,
      }));
  }

  getMaximumCapacity() {
    const capacities = this.getCapacities();
    const calculatedMax = capacities.length ? capacities[capacities.length - 1] : 0;
    
    if (this.options.customMaxCapacity && this.options.customMaxCapacity > 0) {
      return Math.max(calculatedMax, this.options.customMaxCapacity);
    }
    
    return calculatedMax;
  }
}